import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { Alert, Box, Checkbox, FormControl, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import Loader from 'components/shared/loader/loader.component';
import InfoText from 'components/shared/info-text.component';
import StatusDatePicker from 'components/orders/order-details/status-date-picker.component';
import ProductionFinished from 'components/orders/order-details/production-finished.component';
import InvoiceStateModule from 'components/orders/order-details/invoice-state-module.component';
import ProductionValueReportComponent from 'components/shared/production-value-report/production-value-report.component';
import { computeProductionValueSteps } from 'components/shared/production-value-report/production-value-report.utils';
import { hasRestorationAction, restorationActionRowsByActionId } from 'utils/restoration-price-rows';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { getOrderDetailsTemplate, sendOrderAsExternalUserTemplate } from 'apollo/templates/order';
import { getParentChildLabClientComboTemplate } from 'apollo/templates/client-directory/query.graphql';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { ExternalUserInvoiceAction, Order, ProductionValueReport } from 'models/order';
import { LabClient } from 'models/client-directory';
import { InvoiceType } from 'models/invoice';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';
import { resolveClinicInvoiceSettings } from 'utils/invoice-settings';

type ExternalUserSendToClinicDialogProps = {
    onUpdate?: (order: Order) => void;
    onClose: () => void;
    open: boolean;
    orderId?: string;
    onHandleDeliveryNoteCreation?: () => void;
};

const PRODUCTION_FINISHED = 'FINISHED';

/**
 * DD-3391: "Send the order" for an elevated external user.
 *
 * Deliberately its own dialog rather than another branch inside the lab's one.
 * The two differ in what may be chosen - no per-order invoice, no invoice number,
 * no transport - and keeping them apart means neither carries state the other
 * needs. Everything the tier does share with the lab's send, above all marking
 * production finished, behaves exactly as it does there.
 *
 * The approver is the signed-in user and is not editable: an external user cannot
 * read the lab's technician list (DD-3391 closed the directory), and they are the
 * one approving the work.
 */
const ExternalUserSendToClinicDialog: FC<ExternalUserSendToClinicDialogProps> = ({
    onUpdate,
    onClose,
    open,
    orderId,
    onHandleDeliveryNoteCreation,
}: ExternalUserSendToClinicDialogProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'invoices']);
    const { showBoundary } = useDenthubErrorBoundary();
    const currentOrg = currentOrgVar();
    const currentUser = currentUserVar();

    const [order, setOrder] = useState<Order>();
    const [parentClientSettings, setParentClientSettings] = useState<LabClient>({} as LabClient);
    const [childClientSettings, setChildClientSettings] = useState<LabClient>({} as LabClient);
    const [productionState, setProductionState] = useState('');
    const [invoiceState, setInvoiceState] = useState<ExternalUserInvoiceAction>(ExternalUserInvoiceAction.NOT_READY);
    const [showCollectiveInvoicing, setShowCollectiveInvoicing] = useState(false);
    const [isArchive, setIsArchive] = useState(false);
    const [date, setDate] = useState<Date | null>();
    const [isLoading, setLoading] = useState(false);
    const [productionValueReportData, setProductionValueReportData] = useState<{
        orderId: string;
        productionValueReports: ProductionValueReport[];
    }>();
    const [isValidProductionValueReport, setIsValidProductionValueReport] = useState(true);
    // The invoicing defaults below are settled once, when the clinic's settings have
    // arrived. Without this they would be recomputed on the second render and
    // overwrite a choice the user had already made.
    const [areClientSettingsLoaded, setAreClientSettingsLoaded] = useState(false);

    const [getParentChildLabClientCombo, { loading: clientComboLoading }] = useLazyQuery(
        getParentChildLabClientComboTemplate,
        {
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'network-only',
            onCompleted: ({ parentChildLabClientCombo }) => {
                const [parentSettings, childSettings] = parentChildLabClientCombo;
                if (parentSettings?.invoiceEnabled) {
                    setParentClientSettings(parentSettings);
                }
                if (childSettings?.invoiceEnabled) {
                    setChildClientSettings(childSettings);
                }
                setAreClientSettingsLoaded(true);
            },
            onError(error) {
                showBoundary(error);
            },
        },
    );

    const [getOrder] = useLazyQuery(getOrderDetailsTemplate, {
        onCompleted({ order }) {
            setOrder(order);
            if (order) {
                getParentChildLabClientCombo({
                    variables: {
                        parentId: order.customer?._id,
                        childId: order.deliveryDetails?.destination?._id,
                    },
                });
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        getOrder({ variables: { orderId: orderId } });
    }, [orderId]);

    const isOrderInvoiced = !!order?.invoice;
    const orderPrice = order?.deliveryDetails?.price ?? 0;

    // Only a collective clinic gets an invoicing choice here. A per-order clinic's
    // invoice is the lab's to raise, so the send leaves its invoicing untouched.
    useEffect(() => {
        if (!order || !areClientSettingsLoaded) {
            return;
        }
        const { clinicType, activeClientSettings } = resolveClinicInvoiceSettings(
            order,
            parentClientSettings,
            childClientSettings,
            currentOrg,
        );
        const invoiceType = clinicType || (activeClientSettings?.invoiceSettings?.type as string);
        setShowCollectiveInvoicing(
            invoiceType === InvoiceType.COLLECTIVE_INVOICE || invoiceType === InvoiceType.COLLECTIVE_DENTIST,
        );
        if (order.isProductionFinished || isOrderInvoiced) {
            setProductionState(PRODUCTION_FINISHED);
        }
        if (order.isWarrantyOrder && orderPrice === 0) {
            setInvoiceState(ExternalUserInvoiceAction.SHOULD_NOT_INVOICE);
        } else if (order.isProductionFinished && orderPrice > 0) {
            setInvoiceState(ExternalUserInvoiceAction.READY_FOR_INVOICE);
        } else {
            setInvoiceState(ExternalUserInvoiceAction.NOT_READY);
        }
    }, [order, areClientSettingsLoaded]);

    const [sendOrderAsExternalUser] = useMutation(sendOrderAsExternalUserTemplate, {
        onCompleted: ({ sendOrderAsExternalUser: sentOrder }) => {
            onClose();
            onUpdate?.(sentOrder);
            if (productionState === PRODUCTION_FINISHED && onHandleDeliveryNoteCreation) {
                onHandleDeliveryNoteCreation();
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    /**
     * Returns undefined when there is nothing to report, and null when an action has
     * no restoration row to compute against - the lab's dialog abandons the send in
     * that case rather than saving half a report, and so does this one.
     */
    const collectProductionValueReports = () => {
        if (!productionValueReportData) {
            return undefined;
        }
        const actionRowMap = restorationActionRowsByActionId(order?.restorations);
        let hasMissingActionRow = false;
        const reports = productionValueReportData.productionValueReports
            ?.flatMap((productionValueReport) =>
                productionValueReport.actions.map((action) => {
                    const actionRow = actionRowMap.get(action.actionId.toString());
                    if (!actionRow) {
                        hasMissingActionRow = true;
                        return null;
                    }
                    return {
                        actionId: action.actionId,
                        steps: computeProductionValueSteps(action, actionRow, productionValueReport.steps),
                    };
                }),
            )
            .filter(Boolean);
        return hasMissingActionRow ? null : reports;
    };

    const handleSend = async () => {
        setLoading(true);
        const productionValueReports = collectProductionValueReports();
        if (productionValueReports === null) {
            setLoading(false);
            return;
        }
        await sendOrderAsExternalUser({
            variables: {
                orderId: order?._id,
                approvedBy: {
                    fullName: currentUser.fullName,
                    id: currentUser._id || null,
                },
                date: date,
                isProductionFinished: productionState === PRODUCTION_FINISHED,
                invoiceAction: showCollectiveInvoicing ? invoiceState : ExternalUserInvoiceAction.DO_NOTHING,
                isArchive: isArchive,
                productionValueReports: productionValueReports,
            },
        });
        setLoading(false);
    };

    const isFormInvalid =
        !productionState || (productionState === PRODUCTION_FINISHED && !isValidProductionValueReport);

    const hasAtLeastOneAction = hasRestorationAction(order?.restorations);
    const isProductionValueReportEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.PRODUCTION_VALUE_REPORT,
    );

    if (clientComboLoading) {
        return <Loader />;
    }

    return (
        <DHDialog
            fullWidth
            maxWidth="md"
            open={open}
            onClose={onClose}
            title={translate('orders:SEND_THE_ORDER')}
            actions={
                <>
                    <FormControl margin="dense" fullWidth>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={isArchive}
                                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                        setIsArchive(event.target.checked)
                                    }
                                />
                            }
                            label={translate('orders:ARCHIVE_THE_ORDER')}
                            data-test="external-user-send-to-clinic-dialog.archive-order-checkbox"
                        />
                    </FormControl>
                    <LoadingButton
                        sx={{ minWidth: 250 }}
                        disabled={isFormInvalid}
                        variant="contained"
                        onClick={handleSend}
                        loading={isLoading}
                        data-test="external-user-send-to-clinic-dialog.send-button"
                    >
                        {translate('orders:APPROVE_ORDER')}
                    </LoadingButton>
                </>
            }
        >
            <Box>
                <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                    {translate('orders:LAB_APPROVAL_CONFIRMATION_TEXT')}{' '}
                    <strong>{translate('orders:SENT_BY_LAB')}?</strong>
                </Alert>
            </Box>

            <Box mt={2}>
                <Typography variant="h4" mb={1}>
                    {translate('orders:APPROVED_BY')}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                        <TextField
                            fullWidth
                            disabled
                            margin="dense"
                            value={currentUser.fullName || ''}
                            label={translate('common:NAME')}
                            data-test="external-user-send-to-clinic-dialog.approved-by"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <StatusDatePicker onChangeDate={setDate} />
                    </Grid>
                </Grid>
                <InfoText text={translate('orders:EXTERNAL_USER_APPROVED_BY_INSTRUCTIONS')} />
            </Box>

            <Box mt={2} mb={3}>
                <ProductionFinished
                    productionState={productionState}
                    onChangeProduction={setProductionState}
                    isOrderInvoiced={isOrderInvoiced}
                />
            </Box>

            {isProductionValueReportEnabled &&
                order &&
                hasAtLeastOneAction &&
                productionState === PRODUCTION_FINISHED && (
                    <Box mb={3}>
                        <Typography variant="h4" gutterBottom>
                            {translate('orders:PRODUCTION_VALUE_REPORT')}
                        </Typography>
                        <ProductionValueReportComponent
                            order={order}
                            isProductionFinished={true}
                            fromInternalPage={false}
                            isTechnicianAdmin={false}
                            onDataChange={setProductionValueReportData}
                            onValidationChange={setIsValidProductionValueReport}
                        />
                    </Box>
                )}

            {showCollectiveInvoicing ? (
                <Box mt={2} data-test="external-user-send-to-clinic-dialog.collective-invoicing">
                    <Typography variant="h4">{translate('orders:INVOICE_STATE')}</Typography>
                    <InvoiceStateModule
                        invoiceState={invoiceState}
                        onChangeInvoice={(value: string) => setInvoiceState(value as ExternalUserInvoiceAction)}
                        isOrderInvoiced={isOrderInvoiced}
                        order={order}
                    />
                    {isOrderInvoiced ? (
                        <Alert severity="info" variant="outlined" sx={{ border: 0 }}>
                            {translate('orders:ORDER_HAS_BEEN_INVOICED')}
                        </Alert>
                    ) : undefined}
                    {orderPrice <= 0 ? (
                        <Alert severity="info" variant="outlined" sx={{ border: 0 }}>
                            {translate('orders:ZERO_PRICE_ORDER')}
                        </Alert>
                    ) : undefined}
                </Box>
            ) : undefined}
        </DHDialog>
    );
};

export default ExternalUserSendToClinicDialog;
